<script lang="ts">
  import { api, OfflineError } from '../lib/api'
  import { formatDate } from '../lib/format'
  import type { MarketingConsent } from '../lib/types'
  import { ripple } from '../lib/ripple'
  import Notice from '../lib/components/Notice.svelte'
  import Loading from '../lib/components/Loading.svelte'

  /*
   * Marketing preferences — the Website's /account/marketing.
   *
   * THIS IS NOT THE CATEGORY MECHANISM, and must not quietly become it. Design `platform-architecture`
   * §5.2 settles that marketing becomes one category among many on a preference page; §13.9 leaves the
   * migration explicitly unchosen, with "leave it" being what is currently built. So this page reads and
   * writes the existing consent and nothing else, and there is deliberately no `marketing` entry in
   * `EmailCategories.cs` for anything here to write beside it.
   */

  let consent = $state<MarketingConsent | null>(null)
  let updatedAt = $state<string | null>(null)
  let chosen = $state<MarketingConsent>('OptedOut')
  let busy = $state(false)
  let saved = $state(false)
  let failed = $state(false)
  let error = $state<string | null>(null)

  async function load() {
    try {
      const preference = await api.marketingConsent()
      consent = preference.consent
      chosen = preference.consent
      updatedAt = preference.updatedAt
    } catch {
      failed = true
    }
  }

  async function save(event: SubmitEvent) {
    event.preventDefault()
    if (busy) return
    busy = true
    saved = false
    error = null
    try {
      const preference = await api.setMarketingConsent(chosen)
      consent = preference.consent
      updatedAt = preference.updatedAt
      saved = true
    } catch (e) {
      error =
        e instanceof OfflineError
          ? 'Could not reach Bird Journal. Check your connection and try again.'
          : 'We could not save your preference just now. Please try again.'
    } finally {
      busy = false
    }
  }

  // The Save button is pointless until something has actually changed, and a button that does nothing is
  // worse than no button — somebody presses it and cannot tell whether it worked.
  const dirty = $derived(consent !== null && chosen !== consent)

  load()
</script>

{#if failed}
  <Notice tone="error">We could not load your preference just now. Please try again shortly.</Notice>
{:else if consent === null}
  <Loading rows={3} />
{:else}
  <p>We'd like to share occasional Bird Journal news &amp; offers with you by email.</p>
  <p>Please tell us whether you'd like to receive these.</p>

  {#if error}
    <Notice tone="error">{error}</Notice>
  {:else if saved}
    <Notice tone="ok" live>Your preference has been saved.</Notice>
  {/if}

  <form onsubmit={save}>
    <fieldset>
      <legend class="visually-hidden">Marketing emails</legend>

      <label class="choice">
        <input type="radio" name="consent" value="OptedIn" bind:group={chosen} />
        <span>
          <strong>Yes please</strong> — I'd like to receive occasional news &amp; offers about Bird Journal
          by email.
        </span>
      </label>

      <label class="choice">
        <input type="radio" name="consent" value="OptedOut" bind:group={chosen} />
        <span><strong>No thanks</strong></span>
      </label>
    </fieldset>

    <div class="btn-row">
      <button class="btn btn-accent btn-sm rip" use:ripple type="submit" disabled={busy || !dirty}>
        {busy ? 'Saving…' : 'Save'}
      </button>
      {#if updatedAt}
        <span class="stamp">Last changed {formatDate(updatedAt)}</span>
      {/if}
    </div>
  </form>

  <p class="aside">
    This does not affect emails we have to send you about your account — receipts, security alerts and
    password resets.
  </p>
{/if}

<style>
  fieldset {
    border: 0;
    margin: 0 0 var(--space-5);
    padding: 0;
  }
  .stamp {
    color: var(--bj-on-surface-variant);
    font-size: 0.9rem;
  }
  .aside {
    margin-top: var(--space-8);
    color: var(--bj-on-surface-variant);
    font-size: 0.9rem;
  }
</style>
